"use client";

import React from "react";
import { SearchForm } from "@/components/SearchForm";
import { useHotel } from "@/context/HotelContext";
import Link from "next/link";
import { BookOpen, Sparkles, Star, Wifi, Coffee, Waves, Car, Crown } from "lucide-react";

export default function Home() {
  const { hotel, loading } = useHotel();

  // Use hotel's brand colors, fallback to defaults
  const primaryColor = hotel?.primarycolor || "#D4AF37";
  const secondaryColor = hotel?.secondarycolor || "#0A1F44";
  const tertiaryColor = hotel?.tertiary || "#2E2E2E";

  // Convert hex to RGB for gradient
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const primaryRgb = hexToRgb(primaryColor);
  const secondaryRgb = hexToRgb(secondaryColor);

  // Features to display
  const features = [
    { icon: Wifi, label: "Free WiFi" },
    { icon: Coffee, label: "Complimentary Breakfast" },
    { icon: Waves, label: "Swimming Pool" },
    { icon: Car, label: "Free Parking" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100/30 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100/30 flex flex-col items-center justify-start pt-10 p-4 sm:p-6 lg:p-8 relative overflow-hidden font-lato">

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large gradient orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-400/5 to-indigo-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-purple-400/5 to-pink-400/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-300/3 to-blue-400/3 rounded-full blur-3xl" />

        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Background Image if available */}
      {hotel?.url_banner && (
        <div
          className="absolute inset-0 z-0 bg-cover bg-center opacity-5 transition-opacity duration-1000"
          style={{ backgroundImage: `url(${hotel.url_banner})` }}
        />
      )}

      <div className="z-10 w-full max-w-4xl">
        {/* Search Form */}
        <div className="px-2 sm:px-0">
             <SearchForm compact className="border-0 shadow-xl bg-white/90 backdrop-blur-xl" />
        </div>
      </div>
    </div>
  );
}
